import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { BaseProfile, ProfileSelectorConfig } from '@interfaces/profile';
import { SettingsDialogService } from '@components/settings/services/settings-dialog.service';

export type VisibilityFilter = 'all' | 'private' | 'public';

export interface ProfileGroup {
  key: string;
  /** A translation key for "Your profiles", otherwise the organization's own name. */
  label: string;
  translateLabel: boolean;
  profiles: BaseProfile[];
}

/**
 * The profile list on the left of the profile settings dialog.
 *
 * Clicking a row opens that profile in the editor - there is no separate "select" step. The
 * profile on the grid carries an Active badge; applying another one happens from the editor's
 * header. Your own profiles come first, then one group per organization, and the search matches
 * name, owner and organization so a large organization list stays navigable.
 *
 * Organization groups start collapsed, except the one holding the profile being edited - a
 * customer with hundreds of organization profiles would otherwise open on a wall of rows. While a
 * search or visibility filter is active every group with a match is open, since hiding a match
 * behind a collapsed header would read as "no results".
 */
@Component({
  selector: 'app-profile-selector',
  templateUrl: './profile-selector.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileSelectorComponent implements OnChanges {
  @Input() profiles: BaseProfile[];
  /** The profile currently applied to the grid. */
  @Input() lastSelectedProfile: BaseProfile;
  @Input() config: ProfileSelectorConfig;
  @Input() isDirty = false;
  @Input() loading = false;

  @Output() editProfileClicked = new EventEmitter<BaseProfile>();
  @Output() createNewProfileClicked = new EventEmitter<void>();
  @Output() copyProfileClicked = new EventEmitter<BaseProfile>();
  @Output() deleteProfileClicked = new EventEmitter<BaseProfile>();

  editingId: number;
  search = '';
  visibility: VisibilityFilter = 'all';
  groups: ProfileGroup[] = [];
  /** Only groups the user has opened or closed; everything else follows defaultOpen. */
  private openState = new Map<string, boolean>();

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly settingsDialogService: SettingsDialogService,
    private readonly host: ElementRef<HTMLElement>
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.profiles || changes.config) {
      this.buildGroups();
    }

    //the dialog opens on the applied profile, and follows it when another one is applied
    if (changes.lastSelectedProfile?.currentValue && this.config) {
      this.editingId = this.idOf(this.lastSelectedProfile);
      this.editProfileClicked.emit(this.lastSelectedProfile);
      this.scrollToEditing();
    }
  }

  get hasProfiles(): boolean {
    return this.profiles?.length > 0;
  }

  idOf(profile: BaseProfile): number {
    return profile?.[this.config?.profileIdField];
  }

  nameOf(profile: BaseProfile): string {
    return profile?.[this.config?.profileNameField];
  }

  isActive(profile: BaseProfile): boolean {
    return !!this.lastSelectedProfile && this.idOf(profile) === this.idOf(this.lastSelectedProfile);
  }

  trackProfile = (_: number, profile: BaseProfile) => this.idOf(profile);
  trackGroup = (_: number, group: ProfileGroup) => group.key;

  get isFiltering(): boolean {
    return this.search.trim().length > 0 || this.visibility !== 'all';
  }

  onSearch(term: string): void {
    this.search = term ?? '';
    this.buildGroups();
  }

  onVisibility(visibility: VisibilityFilter): void {
    this.visibility = visibility;
    this.buildGroups();
  }

  isOpen(group: ProfileGroup): boolean {
    if (this.isFiltering) return true;
    return this.openState.get(group.key) ?? this.defaultOpen(group);
  }

  toggleGroup(group: ProfileGroup): void {
    if (this.isFiltering) return;
    this.openState.set(group.key, !this.isOpen(group));
  }

  private defaultOpen(group: ProfileGroup): boolean {
    return group.key === 'yours' || group.profiles.some(p => this.idOf(p) === this.editingId);
  }

  /** Opens a profile in the editor. Public: the shell calls it to open a freshly copied profile. */
  editProfile(profile: BaseProfile): void {
    if (this.idOf(profile) === this.editingId) return;
    this.confirmDirtyAndContinue().subscribe(confirmed => {
      if (!confirmed) return;
      this.editingId = this.idOf(profile);
      this.editProfileClicked.emit(profile);
      this.changeDetectorRef.markForCheck();
    });
  }

  copyProfile(profile: BaseProfile): void {
    this.copyProfileClicked.emit(profile);
  }

  deleteProfile(profile: BaseProfile): void {
    this.deleteProfileClicked.emit(profile);
  }

  createNewProfile(): void {
    this.createNewProfileClicked.emit();
  }

  private buildGroups(): void {
    if (!this.config || !this.profiles) {
      this.groups = [];
      return;
    }

    const term = this.search.trim().toLowerCase();
    const matches = this.profiles
      .filter(p => this.visibility === 'all' || (this.visibility === 'private') === !!p.isPrivate)
      .filter(
        p => !term || [this.nameOf(p), p.owner, p.organizationName].some(v => v?.toLowerCase().includes(term))
      );

    const yours = matches.filter(p => p.isPersonal);
    const byOrganization = new Map<string, BaseProfile[]>();
    matches
      .filter(p => !p.isPersonal)
      .forEach(p => {
        const org = p.organizationName ?? '';
        byOrganization.set(org, [...(byOrganization.get(org) ?? []), p]);
      });

    this.groups = [
      ...(yours.length
        ? [{ key: 'yours', label: 'Profiles.YourProfiles', translateLabel: true, profiles: yours }]
        : []),
      ...[...byOrganization.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([org, profiles]) => ({ key: `org:${org}`, label: org, translateLabel: false, profiles })),
    ];
    this.changeDetectorRef.markForCheck();
  }

  //the applied profile can sit far down a long organization list
  private scrollToEditing(): void {
    setTimeout(() =>
      this.host.nativeElement
        .querySelector(`[data-profile-id="${this.editingId}"]`)
        ?.scrollIntoView({ block: 'nearest' })
    );
  }

  private confirmDirtyAndContinue(): Observable<boolean> {
    return this.isDirty ? this.settingsDialogService.confirmBeforeNextAction() : of(true);
  }
}
